import classNames from "classnames";
import { useState } from "react";
import { LogType } from "../../../../../../common/log_types";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { BoxItem } from "../box_item";
import ChannelPicker from "../channel_picker";
import { Disabler } from "../disabler";
import { useServerId } from "../hooks/use_server_id";
import { SettingsBaseItem } from "../settings_items/settings_base_item";
import { SettingsBooleanItem } from "../settings_items/settings_boolean_item";

export function GuildLogChannelSettingsPage(props: { is_premium: boolean }) {
	const server_id = useServerId();

	const query = trpc.guild_logs.getGuildLogChannelSettings.useQuery({
		guild_id: server_id,
	});

	return (
		<div>
			<h1 className="title is-3 has-text-centered">
				Log Channel Settings
			</h1>
			{!query.isSuccess && <>{DashboardUtils.RenderLoadingThrobber()}</>}
			{query.isSuccess && (
				<LoadedGuildLogChannelSettingsPage
					is_premium={props.is_premium}
					guild_log_channel_settings={query.data}
				/>
			)}
		</div>
	);
}

function LoadedGuildLogChannelSettingsPage(props: {
	is_premium: boolean;
	guild_log_channel_settings: RouterInferenceHelpers["guild_logs"]["getGuildLogChannelSettings"]["output"];
}) {
	const server_id = useServerId();

	const trpcContext = trpc.useContext();

	const [enabled, setEnabled] = useState(
		props.guild_log_channel_settings.guild_log_channel_id != null &&
			props.is_premium
	);

	const [data, setData] = useState(props.guild_log_channel_settings);

	const logTypes = Object.keys(LogType)
		.filter((item) => {
			return isNaN(Number(item));
		})
		.map((val) => LogType[val]);

	const mutation = trpc.guild_logs.setGuildLogChannel.useMutation({
		onSuccess: () => {
			trpcContext.guild_logs.getGuildLogChannelSettings.setData(
				(val) => {
					return {
						...val,
						guild_log_channel_id: data.guild_log_channel_id,
						guild_log_channel_disabled_messages:
							data.guild_log_channel_disabled_messages,
					};
				},
				{
					guild_id: server_id,
				}
			);
		},
	});

	return (
		<div className=" flex flex-col gap-2">
			<Disabler disabled={!props.is_premium}>
				<SettingsBooleanItem
					label="Enable Channel Logging"
					description={`${
						!props.is_premium ? "[Premium Only] " : ""
					}Enabling this will send all sesh related logs to a specific Discord channel`}
					value={enabled}
					is_loading={false}
					onChange={() => {
						setEnabled(!enabled);
						mutation.mutate({
							guild_id: server_id,
							channel_id: null,
							disabled_messages:
								data.guild_log_channel_disabled_messages,
						});
					}}
				/>
			</Disabler>

			<Disabler disabled={!enabled}>
				<div className="flex flex-col gap-2">
					<SettingsBaseItem label="Channel" description="">
						<div className="flex w-full flex-col gap-2">
							<ChannelPicker
								channel_id={data.guild_log_channel_id}
								setChannel={(val) =>
									setData((prev) => ({
										...prev,
										guild_log_channel_id: val,
									}))
								}
							/>
						</div>
					</SettingsBaseItem>
					<BoxItem bgColor="semidark" className="flex flex-col gap-4">
						<div
							title={"Events to Log"}
							className={classNames(
								"overflow-hidden overflow-ellipsis font-bold md:text-lg"
							)}
						>
							Events to Log
						</div>
						<div className="flex flex-col gap-2">
							{logTypes.map((logType) => (
								<label className="flex gap-2" key={logType}>
									<input
										type={"checkbox"}
										onChange={() => {
											setData((prev) => ({
												...prev,
												guild_log_channel_disabled_messages:
													{
														...prev.guild_log_channel_disabled_messages,
														[logType]:
															!prev
																.guild_log_channel_disabled_messages?.[
																logType
															],
													},
											}));
										}}
										checked={
											!data
												.guild_log_channel_disabled_messages?.[
												logType
											]
										}
									/>
									<div>{LogType[logType]}</div>
								</label>
							))}
						</div>
					</BoxItem>
					<SeshPrimaryButton
						className="w-fit"
						disabled={!enabled}
						is_loading={mutation.isLoading}
						onClick={() => {
							mutation.mutate({
								guild_id: server_id,
								channel_id: data.guild_log_channel_id,
								disabled_messages:
									data.guild_log_channel_disabled_messages,
							});
						}}
					>
						Save
					</SeshPrimaryButton>
				</div>
			</Disabler>
		</div>
	);
}
